import { useEffect, useState } from "react";
import { Crown, Plus, Trash2, Save, Coins } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const BLANK = {
  name: "",
  min_points: 0,
  min_turnover: 0,
  cashback_pct: 0,
  rakeback_pct: 0,
  color: "#8a97a8",
  perks: [],
  order: 0,
};

export default function VipTiers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(BLANK);

  // VIP Points & Conversion settings (admin controlled)
  const [pointsCfg, setPointsCfg] = useState({
    vip_points_on_deposit_enabled: true,
    vip_points_per_deposit_bdt: 1,
    points_conversion_rate: 100,
    points_min_convert: 100,
  });
  const [savingCfg, setSavingCfg] = useState(false);

  const loadPointsCfg = async () => {
    try {
      const { data } = await api.get("/settings");
      setPointsCfg({
        vip_points_on_deposit_enabled: data.vip_points_on_deposit_enabled ?? true,
        vip_points_per_deposit_bdt: Number(data.vip_points_per_deposit_bdt ?? 1),
        points_conversion_rate: Number(data.points_conversion_rate ?? 100),
        points_min_convert: Number(data.points_min_convert ?? 100),
      });
    } catch {
      // silent — will use defaults
    }
  };

  const savePointsCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/settings", {
        vip_points_on_deposit_enabled: !!pointsCfg.vip_points_on_deposit_enabled,
        vip_points_per_deposit_bdt: Number(pointsCfg.vip_points_per_deposit_bdt) || 0,
        points_conversion_rate: Number(pointsCfg.points_conversion_rate) || 100,
        points_min_convert: Math.max(1, parseInt(pointsCfg.points_min_convert) || 1),
      });
      toast.success("Points settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingCfg(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vip-tiers");
      setRows(data || []);
    } catch {
      toast.error("Failed to load tiers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadPointsCfg(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/vip-tiers", form);
      toast.success("Tier created");
      setForm(BLANK);
      setCreating(false);
      load();
    } catch {
      toast.error("Failed to create tier");
    }
  };

  const patch = async (id, upd) => {
    try {
      await api.patch(`/vip-tiers/${id}`, upd);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tier?")) return;
    try {
      await api.delete(`/vip-tiers/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="vip-tiers-page">
      <PageHeader
        title="VIP Tiers"
        subtitle="Loyalty tiers · turnover thresholds, instant rakeback %, weekly cashback %, perks"
      />

      {/* ---------- VIP Points & Conversion (admin-controlled) ---------- */}
      <div className="bg-white border border-gray-200 rounded-sm mb-5" data-testid="vip-points-config-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Coins size={14} className="text-amber-500" />
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
              Points on Deposit & Points → Balance Conversion
            </div>
          </div>
          <button
            type="button"
            onClick={savePointsCfg}
            disabled={savingCfg}
            className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5"
            data-testid="vip-points-save-btn"
          >
            <Save size={14} /> {savingCfg ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Award points on deposit">
            <label className="inline-flex items-center gap-2 h-10 px-3 border border-gray-200 rounded-sm text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!pointsCfg.vip_points_on_deposit_enabled}
                onChange={(e) => setPointsCfg({ ...pointsCfg, vip_points_on_deposit_enabled: e.target.checked })}
                data-testid="vip-points-enabled-toggle"
              />
              <span>{pointsCfg.vip_points_on_deposit_enabled ? "Enabled" : "Disabled"}</span>
            </label>
          </Field>
          <Field label="Points per 1 ৳ deposited">
            <input
              type="number"
              step="0.01"
              min="0"
              value={pointsCfg.vip_points_per_deposit_bdt}
              onChange={(e) => setPointsCfg({ ...pointsCfg, vip_points_per_deposit_bdt: e.target.value })}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
              data-testid="vip-points-per-bdt-input"
            />
          </Field>
          <Field label="Points needed for 1 ৳ cash">
            <input
              type="number"
              step="1"
              min="1"
              value={pointsCfg.points_conversion_rate}
              onChange={(e) => setPointsCfg({ ...pointsCfg, points_conversion_rate: e.target.value })}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
              data-testid="vip-points-conversion-input"
            />
          </Field>
          <Field label="Minimum points to convert">
            <input
              type="number"
              step="1"
              min="1"
              value={pointsCfg.points_min_convert}
              onChange={(e) => setPointsCfg({ ...pointsCfg, points_min_convert: e.target.value })}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
              data-testid="vip-points-min-convert-input"
            />
          </Field>
          <div className="md:col-span-4 text-xs text-gray-500 leading-relaxed bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="vip-points-preview">
            <b>Preview:</b>{" "}
            {pointsCfg.vip_points_on_deposit_enabled
              ? <>User depositing ৳1,000 will earn{" "}
                  <span className="text-emerald-600 font-semibold">
                    {Math.floor(1000 * (Number(pointsCfg.vip_points_per_deposit_bdt) || 0))} VIP points
                  </span>
                  .{" "}
                </>
              : <>Points on deposit is <b>disabled</b>.{" "}</>
            }
            {Number(pointsCfg.points_conversion_rate) > 0 && (
              <>
                To convert to cash, <b>{Number(pointsCfg.points_conversion_rate)}</b> points = <b>৳1</b>.{" "}
                Minimum <b>{Number(pointsCfg.points_min_convert)}</b> points required to convert.
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm mb-5">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Crown size={14} className="text-emerald-500" />
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Tiers</div>
          </div>
          <button type="button" onClick={() => setCreating((s) => !s)} className="h-9 px-3 bg-gray-900 hover:bg-gray-800 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5" data-testid="vip-add-toggle">
            <Plus size={14} /> {creating ? "Cancel" : "New tier"}
          </button>
        </div>

        {creating && (
          <div className="p-5 border-b border-gray-100 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="vip-create-form">
            <Field label="Name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="vip-new-name" />
            </Field>
            <Field label="Min turnover ৳">
              <input type="number" step="1" value={form.min_turnover} onChange={(e) => setForm({ ...form, min_turnover: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="vip-new-min-turnover" />
            </Field>
            <Field label="Rakeback %">
              <input type="number" step="0.01" value={form.rakeback_pct} onChange={(e) => setForm({ ...form, rakeback_pct: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="vip-new-rakeback" />
            </Field>
            <Field label="Cashback %">
              <input type="number" step="0.01" value={form.cashback_pct} onChange={(e) => setForm({ ...form, cashback_pct: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="vip-new-cashback" />
            </Field>
            <Field label="Min points (legacy)">
              <input type="number" value={form.min_points} onChange={(e) => setForm({ ...form, min_points: parseInt(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
            <Field label="Order">
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
            </Field>
            <div className="md:col-span-3">
              <Field label="Color">
                <div className="flex gap-2">
                  <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-12 border border-gray-200 rounded-sm" />
                  <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="flex-1 h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono" />
                </div>
              </Field>
            </div>
            <div className="md:col-span-6">
              <Field label="Perks (one per line)">
                <textarea rows={3} value={(form.perks || []).join("\n")} onChange={(e) => setForm({ ...form, perks: e.target.value.split("\n").filter(Boolean) })} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" />
              </Field>
            </div>
            <div className="md:col-span-6 flex justify-end">
              <button type="button" onClick={create} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="vip-create-submit">
                <Save size={14} /> Create
              </button>
            </div>
          </div>
        )}

        <div className="p-5 space-y-3">
          {loading ? (
            <div className="text-sm text-gray-500 text-center py-6">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-6 border border-dashed border-gray-200 rounded-sm">No tiers. Click "New tier" above.</div>
          ) : rows.map((t) => (
            <div key={t.id} className="border border-gray-200 rounded-sm p-4" data-testid={`vip-row-${t.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="h-8 w-8 rounded-md" style={{ background: `linear-gradient(135deg, ${t.color} 0%, #1a2030 100%)` }} />
                <input value={t.name} onChange={(e) => patch(t.id, { name: e.target.value })} className="flex-1 h-10 px-3 border border-gray-200 rounded-sm text-sm font-semibold" />
                <button type="button" onClick={() => remove(t.id)} className="p-2 rounded-sm hover:bg-rose-50 text-rose-500" data-testid={`vip-del-${t.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <Field label="Min turnover ৳">
                  <input type="number" step="1" value={t.min_turnover ?? 0} onChange={(e) => patch(t.id, { min_turnover: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid={`vip-min-turnover-${t.id}`} />
                </Field>
                <Field label="Rakeback %">
                  <input type="number" step="0.01" value={t.rakeback_pct ?? 0} onChange={(e) => patch(t.id, { rakeback_pct: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid={`vip-rakeback-${t.id}`} />
                </Field>
                <Field label="Cashback %">
                  <input type="number" step="0.01" value={t.cashback_pct ?? 0} onChange={(e) => patch(t.id, { cashback_pct: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
                </Field>
                <Field label="Min points (legacy)">
                  <input type="number" value={t.min_points ?? 0} onChange={(e) => patch(t.id, { min_points: parseInt(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
                </Field>
                <Field label="Color">
                  <div className="flex gap-2">
                    <input type="color" value={t.color || "#8a97a8"} onChange={(e) => patch(t.id, { color: e.target.value })} className="h-10 w-12 border border-gray-200 rounded-sm" />
                    <input value={t.color || ""} onChange={(e) => patch(t.id, { color: e.target.value })} className="flex-1 h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono" />
                  </div>
                </Field>
                <Field label="Order">
                  <input type="number" value={t.order ?? 0} onChange={(e) => patch(t.id, { order: parseInt(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" />
                </Field>
                <div className="md:col-span-6">
                  <Field label="Perks (one per line)">
                    <textarea rows={3} value={(t.perks || []).join("\n")} onChange={(e) => patch(t.id, { perks: e.target.value.split("\n").filter(Boolean) })} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" />
                  </Field>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
